import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { journeyFAQ } from '../data/siteData';
import StoreButtons from '../components/StoreButtons';

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.fade-up');
    if (!els.length) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg className="faq-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>{question}</span>
        <ChevronDown />
      </button>
      <div className="faq-answer">{answer}</div>
    </div>
  );
}

const timelineItems = [
  {
    week: 'Day 1',
    title: 'Sign in and set your budget.',
    desc: 'You download Klar, sign in with Google, enter your name and phone, then set your monthly budget — say ₹8,000. You split it: ₹2,500 mess, ₹1,200 food outside, ₹600 transport, ₹1,500 hostel, ₹400 personal care, ₹600 entertainment, ₹800 savings, ₹400 misc. Takes about 3 minutes. Then you close the app and go to class.',
  },
  {
    week: 'Week 1',
    title: 'The habit starts forming.',
    desc: 'You start logging expenses. The first few times feel slightly deliberate — open app, tap category, type amount, confirm. By day 4, it\'s automatic. You\'re logging the ₹30 chai, the ₹120 auto ride, the ₹380 Zomato order. You notice your Entertainment category is already at 60% on day 5. That\'s the awareness kicking in.',
  },
  {
    week: 'Weeks 2–3',
    title: 'Patterns become visible.',
    desc: 'The Insights screen starts telling you things you didn\'t know. You\'re spending an average of ₹340 per day on food outside, which annualises to over ₹1.2 lakh. Your mess category is underspent because you\'ve been skipping meals. You make one change: eat mess breakfast every day. Food outside drops by ₹400 in week 3.',
  },
  {
    week: 'Week 4',
    title: 'You\'re on a 21-day streak.',
    desc: 'End of month. Your savings category is untouched — ₹800 actually saved. Total spend: ₹7,620 against an ₹8,000 budget. ₹380 carried forward. Doesn\'t sound like much, but that\'s the first time you\'ve finished a month with money left. Streak: 21 days. You won\'t break it next month.',
  },
];

const habits = [
  {
    emoji: '☀️',
    bg: 'rgba(200,241,53,0.08)',
    title: 'Morning check-in',
    desc: 'Open Klar in the morning to see yesterday\'s total and check how the week is going. Takes 15 seconds.',
  },
  {
    emoji: '🍜',
    bg: 'rgba(249,115,22,0.08)',
    title: 'Log before you leave',
    desc: 'Log the expense while you\'re still at the dhaba, auto stand, or canteen. Don\'t rely on memory later.',
  },
  {
    emoji: '📊',
    bg: 'rgba(59,130,246,0.08)',
    title: 'Weekly review on Sundays',
    desc: 'Spend 5 minutes looking at the Insights screen every Sunday. One category that surprised you. Adjust next week.',
  },
  {
    emoji: '🔥',
    bg: 'rgba(239,68,68,0.08)',
    title: 'Protect the streak',
    desc: 'The streak is intentionally simple — but it works. You won\'t want to reset it. That\'s enough motivation.',
  },
  {
    emoji: '💸',
    bg: 'rgba(16,185,129,0.08)',
    title: 'Savings first, spend the rest',
    desc: 'Log your savings allocation on day 1 of every month, before spending anything. Set aside what you\'re saving, not what\'s left.',
  },
  {
    emoji: '🎯',
    bg: 'rgba(139,92,246,0.08)',
    title: 'Honest logging',
    desc: 'Log every expense, including the embarrassing ones. The app doesn\'t judge — it just shows you patterns. That\'s the whole point.',
  },
];

export default function StudentJourneyPage() {
  useScrollReveal();

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="inner-hero">
        <div className="inner-hero-bg"/>
        <div className="container">
          <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
            <div className="section-label fade-up">Student Journey</div>
            <h1 className="text-display fade-up delay-1">
              What a month with <span className="text-gradient">Klar</span> actually looks like.
            </h1>
            <p className="text-body-lg mt-6 fade-up delay-2" style={{ maxWidth: 520, margin: '24px auto 40px' }}>
              Not a marketing story. A realistic walkthrough of how a college student goes from "I have no idea where my money goes" to actually knowing.
            </p>
            <div className="fade-up delay-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
              <StoreButtons className="center" size="lg" />
              <Link to="/product" className="btn btn-ghost btn-sm">
                See the Product <ArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Timeline ─────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="feature-split" style={{ gap: 64, alignItems: 'start' }}>
            {/* Left: intro */}
            <div className="fade-up">
              <div className="section-label">The Month</div>
              <h2 className="text-headline" style={{ marginBottom: 16 }}>
                Four weeks. Real change.
              </h2>
              <p className="text-body" style={{ marginBottom: 24 }}>
                This is a composite of what real students experienced in their first month with Klar. The spending numbers are real averages. The habits are real. The savings are real.
              </p>
              <div style={{ background: 'var(--lime-glow)', border: '1px solid rgba(200,241,53,0.15)', borderRadius: 'var(--radius-md)', padding: '20px 24px' }}>
                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--lime)', marginBottom: 8 }}>Student average</div>
                <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>₹520</div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Average monthly saving in first month with Klar</div>
              </div>
            </div>

            {/* Right: timeline */}
            <div className="timeline fade-up delay-2">
              {timelineItems.map(item => (
                <div key={item.week} className="timeline-item">
                  <div className="timeline-dot"/>
                  <div className="timeline-week">{item.week}</div>
                  <h3 className="timeline-title">{item.title}</h3>
                  <p className="timeline-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Student types ────────────────────────────────────────────────── */}
      <section className="section" style={{ background: 'var(--bg-alt)' }}>
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">Who It's For</div>
            <h2 className="text-headline">Hostellers and day scholars both.</h2>
            <p className="text-body-lg mt-4">
              Klar adapts to your situation. The categories are the same — the amounts are yours to set.
            </p>
          </div>

          <div className="grid grid-2 fade-up delay-2" style={{ gap: 24 }}>
            {/* Hosteller card */}
            <div className="card-elevated" style={{ borderRadius: 'var(--radius-lg)', padding: 32 }}>
              <div style={{ fontSize: '2rem', marginBottom: 16 }}>🏠</div>
              <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Hosteller</h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-relaxed)', marginBottom: 20 }}>
                Mess food is your base. Food outside is the variable you control. Transport to college is fixed. Laundry, room extras, and electricity bills go under Hostel.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { cat: 'Mess', amount: '₹3,000', color: '#C8F135' },
                  { cat: 'Food Outside', amount: '₹1,500', color: '#F97316' },
                  { cat: 'Transport', amount: '₹400', color: '#3B82F6' },
                  { cat: 'Hostel Extras', amount: '₹800', color: '#8B5CF6' },
                ].map(item => (
                  <div key={item.cat} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--text-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }}/>
                      <span style={{ color: 'var(--text-secondary)' }}>{item.cat}</span>
                    </div>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{item.amount}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Day scholar card */}
            <div className="card-elevated" style={{ borderRadius: 'var(--radius-lg)', padding: 32 }}>
              <div style={{ fontSize: '2rem', marginBottom: 16 }}>🚌</div>
              <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Day Scholar</h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-relaxed)', marginBottom: 20 }}>
                Transport is your biggest variable — bus pass, metro, autos. Canteen and outside food replace mess. The Hostel category becomes Home Extras for anything home-related.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { cat: 'Transport', amount: '₹1,800', color: '#3B82F6' },
                  { cat: 'Food Outside', amount: '₹2,000', color: '#F97316' },
                  { cat: 'Canteen (Mess)', amount: '₹800', color: '#C8F135' },
                  { cat: 'Home Extras', amount: '₹600', color: '#8B5CF6' },
                ].map(item => (
                  <div key={item.cat} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--text-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }}/>
                      <span style={{ color: 'var(--text-secondary)' }}>{item.cat}</span>
                    </div>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{item.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Habits ───────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">Student Habits</div>
            <h2 className="text-headline">Six habits that make Klar actually work.</h2>
            <p className="text-body-lg mt-4">
              The app is simple by design. The habits are what make the difference.
            </p>
          </div>

          <div className="habits-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', display: 'grid', gap: 'var(--space-5)' }}>
            {habits.map((h, i) => (
              <div key={h.title} className={`habit-card fade-up delay-${(i % 3) + 1}`}>
                <div className="habit-icon" style={{ background: h.bg }}>{h.emoji}</div>
                <div>
                  <div className="habit-title">{h.title}</div>
                  <div className="habit-desc">{h.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="section" style={{ background: 'var(--bg-alt)' }}>
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">FAQ</div>
            <h2 className="text-headline">Questions about using Klar daily.</h2>
          </div>
          <div className="faq-list fade-up delay-2">
            {journeyFAQ.map(item => (
              <FAQItem key={item.q} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <div className="cta-banner">
        <div className="container">
          <div className="cta-banner-content">
            <h2 className="cta-banner-headline">Ready to start your first month?</h2>
            <p className="cta-banner-sub">
              Download Klar — it takes 3 minutes to set up. The first insight is usually within a week.
            </p>
            <StoreButtons className="center" size="lg" />
          </div>
        </div>
      </div>
    </>
  );
}
